import React from 'react';

import { makeStyles } from '@material-ui/core/styles';

import { Translate } from '@hydra/common/components';
import SteppedProgressBar from '@hydra/common/components/SteppedProgressBar';
import { Criterium, CriteriumSkeleton, CriteriumCompetitors } from '@hydra/common/criterium';
import { useReviewContext, usePdfTemplateContext } from '@hydra/common/context';

import { getContrastedColor, getPriSecColors } from '@hydra/utils/colors';
import hasCritData from '@hydra/utils/hasCritData';

import { CriteriumInterface, ReviewData } from '@hydra/types/review';

const useStyles = makeStyles(theme => ({
  rank: {
    marginBottom: theme.spacing(1)
  }
}));

interface RankingProps {
  percent: number
  value: React.ReactNode
}

const RANK_VALUES = [
  'very_low',
  'very_low',
  'low',
  'medium',
  'high',
  'very_high'
];

export const getRanking = (score: number | null) : RankingProps => {
  const points = Math.ceil(((score === null) ? 0 : score * 10) / 2);
  return {
    percent: points * 20,
    value: <Translate content={`${RANK_VALUES[points] ? RANK_VALUES[points] : RANK_VALUES[0]}`} />
  };
};

export const TrafficEstimation = ({
  criterium,
  competitors = []
}: {
  criterium: CriteriumInterface
  competitors: Array<ReviewData>
}) => {
  const classes = useStyles();
  const { isPdf, variant } = useReviewContext();
  const { templateColors } = usePdfTemplateContext();

  const { primary, secondary } = isPdf
    ? templateColors
    : getPriSecColors();
  const color = getContrastedColor(primary);
  const { url, type, score } = criterium;

  const singleContent = (
    <>
      <div className={classes.rank}>{variant === 'nocrit' ? <CriteriumSkeleton width='30%' /> : getRanking(score).value}</div>
      {
        variant === 'nocrit'
          ? <CriteriumSkeleton />
          : <SteppedProgressBar
              value={getRanking(score).percent}
              color={color}
              max={100}
            />
      }
    </>
  );
  let compContent;
  if (competitors.length > 0) {
    const compComponents = [
      { component: singleContent, url },
      ...competitors.map(({ criteria }, index) => {
        if (!hasCritData(criteria[type])) {
          return null;
        }
        return {
          component: (
            <>
              <div className={classes.rank}>{variant === 'nocrit' ? <CriteriumSkeleton width='30%' /> : getRanking(criteria[type].score).value}</div>
              {
                variant === 'nocrit'
                  ? <CriteriumSkeleton />
                  : <SteppedProgressBar
                      value={getRanking(criteria[type].score).percent}
                      color={secondary[index]}
                      max={100}
                    />
              }
            </>
          ),
          url: criteria[type].url
        };
      })
    ];
    compContent = <CriteriumCompetitors competitors={compComponents} />;
  }

  return (
    <Criterium
      criterium={criterium}
      variables={{ domain: url, url }}
    >
      {compContent || singleContent}
    </Criterium>
  );
};

export default React.memo(TrafficEstimation);
